import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, PackageCheck, RefreshCw, Search, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/** Format an ISO date/date-string as "15 Mar 2026" (or "TBC" if missing/invalid). */
const fmtDate = (v) => {
    if (!v) return "TBC";
    try {
        const d = String(v).length === 10 ? new Date(v + "T00:00:00Z") : new Date(v);
        if (isNaN(d.getTime())) return String(v);
        return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return String(v);
    }
};

const AgingBadge = ({ days }) => {
    if (days === null || days === undefined) {
        return (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-white/10 text-[var(--text-muted)]">
                TBC
            </span>
        );
    }
    if (days < 0) {
        return (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-red-500/40 bg-red-500/10 text-red-300">
                Overdue · {Math.abs(days)}d
            </span>
        );
    }
    if (days <= 3) {
        return (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-amber-500/40 bg-amber-500/10 text-amber-300">
                Due in {days}d
            </span>
        );
    }
    return (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
            In {days}d
        </span>
    );
};

export const AdminPreOrdersPanel = ({ token }) => {
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all"); // all | ready | pending | overdue
    const [busySlug, setBusySlug] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/pre-orders/lines", {
                headers: { "X-Admin-Token": token },
            });
            setLines(r.data.lines || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load pre-orders");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const toggleReady = async (line) => {
        const nextValue = !line.ready_to_ship;
        const key = `${line.reference}::${line.product_slug}`;
        setBusySlug(key);
        try {
            await api.post(
                `/admin/pre-orders/${encodeURIComponent(line.reference)}/${encodeURIComponent(line.product_slug)}/toggle-ready`,
                { ready: nextValue },
                { headers: { "X-Admin-Token": token } },
            );
            setLines((rows) => rows.map((r) =>
                r.reference === line.reference && r.product_slug === line.product_slug
                    ? { ...r, ready_to_ship: nextValue, marked_ready_at: nextValue ? new Date().toISOString() : null }
                    : r
            ));
            toast.success(nextValue ? "Marked ready to ship" : "Reverted to pending");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toggle failed");
        } finally {
            setBusySlug(null);
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return lines.filter((l) => {
            if (filter === "ready" && !l.ready_to_ship) return false;
            if (filter === "pending" && l.ready_to_ship) return false;
            if (filter === "overdue" && !(l.days_until_dispatch !== null && l.days_until_dispatch < 0)) return false;
            if (!q) return true;
            return (
                (l.product_name || "").toLowerCase().includes(q) ||
                (l.product_slug || "").toLowerCase().includes(q) ||
                (l.reference || "").toLowerCase().includes(q) ||
                (l.customer_email || "").toLowerCase().includes(q) ||
                (l.customer_name || "").toLowerCase().includes(q)
            );
        });
    }, [lines, query, filter]);

    const counts = useMemo(() => ({
        total: lines.length,
        ready: lines.filter((l) => l.ready_to_ship).length,
        pending: lines.filter((l) => !l.ready_to_ship).length,
        overdue: lines.filter((l) => l.days_until_dispatch !== null && l.days_until_dispatch < 0).length,
        units: lines.reduce((s, l) => s + (l.quantity || 0), 0),
    }), [lines]);

    return (
        <div className="space-y-5" data-testid="admin-preorders-panel">
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display text-2xl font-bold text-white flex items-center gap-2">
                        <PackageCheck size={22} className="text-[var(--gold)]" /> Pre-Order Fulfilment
                    </h2>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                        Every paid pre-order line item, aged by estimated dispatch date. Toggle "Ready to ship" once stock lands.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="btn-neon"
                    data-testid="admin-preorders-refresh"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {/* Stats tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-preorders-stats">
                {[
                    { label: "Total lines", value: counts.total, tone: "text-white" },
                    { label: "Units due", value: counts.units, tone: "text-[var(--gold)]" },
                    { label: "Ready to ship", value: counts.ready, tone: "text-emerald-300" },
                    { label: "Overdue", value: counts.overdue, tone: "text-red-300" },
                ].map((t) => (
                    <div key={t.label} className="glass-elevated rounded-2xl p-4">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{t.label}</p>
                        <p className={`font-display text-2xl font-bold mt-1 ${t.tone}`}>{t.value}</p>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by product, reference, customer…"
                        className="w-full pl-9 pr-4 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-preorders-search"
                    />
                </div>
                {["all", "pending", "ready", "overdue"].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-2 rounded-full text-[11px] font-mono uppercase tracking-widest border ${
                            filter === f
                                ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                        }`}
                        data-testid={`admin-preorders-filter-${f}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="glass-elevated rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[820px]">
                        <thead className="bg-black/40 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                            <tr>
                                <th className="px-4 py-3 text-left">Product</th>
                                <th className="px-4 py-3 text-left">Order</th>
                                <th className="px-4 py-3 text-left">Customer</th>
                                <th className="px-4 py-3 text-right">Qty</th>
                                <th className="px-4 py-3 text-left">Dispatch date</th>
                                <th className="px-4 py-3 text-left">Aging</th>
                                <th className="px-4 py-3 text-right">Ready</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="text-center py-10 text-[var(--text-muted)]">Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center py-10 text-[var(--text-muted)]">
                                        No pre-order lines match this filter.
                                    </td>
                                </tr>
                            )}
                            {!loading && filtered.map((l) => {
                                const key = `${l.reference}::${l.product_slug}`;
                                const busy = busySlug === key;
                                return (
                                    <tr
                                        key={key}
                                        className={`border-t border-white/5 ${l.ready_to_ship ? "bg-emerald-500/[0.04]" : ""}`}
                                        data-testid={`preorder-row-${l.reference}-${l.product_slug}`}
                                    >
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/product/${l.product_slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="font-display font-semibold text-white hover:text-[var(--gold)]"
                                            >
                                                {l.product_name}
                                            </Link>
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-0.5">
                                                {l.product_slug}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/admin?order=${l.reference}`}
                                                className="font-mono text-[var(--gold)] hover:underline"
                                            >
                                                {l.reference}
                                            </Link>
                                            {l.mixed_order_choice && (
                                                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--neon-cyan)] mt-0.5">
                                                    {l.mixed_order_choice === "ship_separately" ? "Ship separately" : "Ship together"}
                                                </div>
                                            )}
                                            {l.shipping_method_name && (
                                                <div className="text-[10px] text-[var(--text-muted)] mt-0.5 inline-flex items-center gap-1">
                                                    <Truck size={10} /> {l.shipping_method_name}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-white text-sm">{l.customer_name || "—"}</div>
                                            <div className="text-[11px] text-[var(--text-muted)]">{l.customer_email || "—"}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-white">×{l.quantity}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-white text-sm inline-flex items-center gap-1">
                                                <Clock size={12} className="text-[var(--text-muted)]" /> {fmtDate(l.estimated_dispatch_date)}
                                            </div>
                                            {l.expected_arrival_date && (
                                                <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                                                    Stock arrives {fmtDate(l.expected_arrival_date)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <AgingBadge days={l.days_until_dispatch} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => toggleReady(l)}
                                                disabled={busy}
                                                data-testid={`preorder-toggle-${l.reference}-${l.product_slug}`}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-all ${
                                                    l.ready_to_ship
                                                        ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                                                        : "border-[rgba(0,242,254,0.4)] text-[var(--neon-cyan)] hover:bg-[rgba(0,242,254,0.1)]"
                                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                            >
                                                {l.ready_to_ship ? <><Check size={12} /> Ready</> : <><PackageCheck size={12} /> Mark ready</>}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminPreOrdersPanel;
